import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getBrandingHostValue } from '@core/tenant'
import { useToast } from '@shared/hooks/useToast'
import { userMessageManager } from '@shared/lib/messages/userMessageManager'
import {
  deleteBrandingLogo,
  getCurrentTenantBranding,
  resolveTenantBranding,
  updateCurrentTenantBranding,
  uploadBrandingLogo,
} from '../services/branding.service'
import type { TenantBrandingResponse, UpdateTenantBrandingDto } from '../types/branding.types'

export const tenantBrandingQueryKeys = {
  resolve: (host?: string) => ['tenant-branding', 'resolve', host ?? 'default'] as const,
  current: () => ['tenant-branding', 'current'] as const,
}

export function useTenantBrandingBootstrap() {
  const host = getBrandingHostValue()

  return useQuery({
    queryKey: tenantBrandingQueryKeys.resolve(host),
    queryFn: async () => {
      const response = await resolveTenantBranding(host)
      return response.data ?? null
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
    enabled: Boolean(host),
  })
}

export function useCurrentTenantBranding() {
  return useQuery({
    queryKey: tenantBrandingQueryKeys.current(),
    queryFn: async () => {
      const response = await getCurrentTenantBranding()
      return response.data as TenantBrandingResponse
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdateTenantBranding() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: UpdateTenantBrandingDto) => updateCurrentTenantBranding(data),
    onSuccess: (response) => {
      queryClient.setQueryData(tenantBrandingQueryKeys.current(), response.data)

      const host = response.data?.domain ?? getBrandingHostValue()
      queryClient.setQueryData(tenantBrandingQueryKeys.resolve(host ?? undefined), response.data ?? null)
      queryClient.invalidateQueries({ queryKey: tenantBrandingQueryKeys.resolve(host ?? undefined) })

      toast(userMessageManager.resolveMessage('branding.update.success'))
    },
    onError: (error) => {
      toast(userMessageManager.resolveErrorMessage({
        error,
        defaultMessageKey: 'branding.update.error',
      }))
    },
  })
}

export function useUploadTenantLogo() {
  const { toast } = useToast()

  return useMutation({
    mutationFn: (file: File) => uploadBrandingLogo(file),
    onError: (error) => {
      toast(userMessageManager.resolveErrorMessage({
        error,
        defaultMessageKey: 'branding.upload.error',
      }))
    },
  })
}

export function useDeleteTenantLogo() {
  return useMutation({
    mutationFn: (fileUrl?: string | null) => deleteBrandingLogo(fileUrl),
  })
}
